import { InboundEmailInboxQuery, InboundEmailQuery } from '@/gql/graphql';
import { InfiniteData } from '@tanstack/react-query';
import { produce } from 'immer';

type InboxItem =
  InboundEmailInboxQuery['me']['inboundEmailInbox']['items'][number];

type DetailItem = NonNullable<InboundEmailQuery['me']['inboundEmail']>;

type EmailItem = InboxItem | DetailItem;

function findEmailInPages(
  pages: InfiniteData<InboundEmailInboxQuery>['pages'],
  inboundEmailId: string
): InboxItem | undefined {
  for (const page of pages) {
    const item = page.me.inboundEmailInbox.items.find(
      (i) => i.id === inboundEmailId
    );
    if (item) return item;
  }
  return undefined;
}

// --- Transform functions that operate on a mutable email item ---

type AssignVariables = {
  projectId: string;
  projectReference: string;
};

function transformArchive(item: EmailItem): boolean {
  const wasUnread = item.status === 'UNREAD';
  item.status = 'ARCHIVED';
  item.assignedProject = null;
  return wasUnread;
}

function transformUnarchive(item: EmailItem): boolean {
  item.status = 'READ';
  return false;
}

function transformMarkAsRead(item: EmailItem): boolean {
  const wasUnread = item.status === 'UNREAD';
  item.status = 'READ';
  return wasUnread;
}

function transformAssignToProject(
  item: EmailItem,
  variables: AssignVariables
): boolean {
  item.assignedProject = {
    __typename: 'Project',
    id: variables.projectId,
    publicId: variables.projectId,
    reference: variables.projectReference,
  };
  item.status = 'ASSIGNED';
  return false;
}

function transformUnassign(item: EmailItem): boolean {
  item.assignedProject = null;
  item.status = 'READ';
  return false;
}

// --- Generic wrappers ---

function applyToInbox<V extends { inboundEmailId: string }>(
  data: InfiniteData<InboundEmailInboxQuery>,
  variables: V,
  transform: (item: EmailItem, variables: V) => boolean
): { data: InfiniteData<InboundEmailInboxQuery>; wasUnread: boolean } {
  let wasUnread = false;

  const updated = produce(data, (draft) => {
    const item = findEmailInPages(draft.pages, variables.inboundEmailId);
    if (!item) return;

    wasUnread = transform(item, variables);
  });

  return { data: updated, wasUnread };
}

function applyToDetail<V>(
  data: InboundEmailQuery,
  variables: V,
  transform: (item: EmailItem, variables: V) => boolean
): { data: InboundEmailQuery; wasUnread: boolean } {
  let wasUnread = false;

  const updated = produce(data, (draft) => {
    if (!draft.me.inboundEmail) return;

    wasUnread = transform(draft.me.inboundEmail, variables);
  });

  return { data: updated, wasUnread };
}

// --- Exported inbox functions ---

export function applyOptimisticArchive(
  data: InfiniteData<InboundEmailInboxQuery>,
  variables: { inboundEmailId: string }
): { data: InfiniteData<InboundEmailInboxQuery>; wasUnread: boolean } {
  return applyToInbox(data, variables, transformArchive);
}

export function applyOptimisticUnarchive(
  data: InfiniteData<InboundEmailInboxQuery>,
  variables: { inboundEmailId: string }
): { data: InfiniteData<InboundEmailInboxQuery> } {
  return applyToInbox(data, variables, transformUnarchive);
}

export function applyOptimisticMarkAsRead(
  data: InfiniteData<InboundEmailInboxQuery>,
  variables: { inboundEmailId: string }
): { data: InfiniteData<InboundEmailInboxQuery>; wasUnread: boolean } {
  return applyToInbox(data, variables, transformMarkAsRead);
}

export function applyOptimisticAssignToProject(
  data: InfiniteData<InboundEmailInboxQuery>,
  variables: {
    inboundEmailId: string;
    projectId: string;
    projectReference: string;
  }
): { data: InfiniteData<InboundEmailInboxQuery> } {
  return applyToInbox(data, variables, transformAssignToProject);
}

export function applyOptimisticUnassign(
  data: InfiniteData<InboundEmailInboxQuery>,
  variables: { inboundEmailId: string }
): { data: InfiniteData<InboundEmailInboxQuery> } {
  return applyToInbox(data, variables, transformUnassign);
}

// --- Exported detail functions ---

export function applyDetailOptimisticArchive(data: InboundEmailQuery): {
  data: InboundEmailQuery;
  wasUnread: boolean;
} {
  return applyToDetail(data, undefined, transformArchive);
}

export function applyDetailOptimisticUnarchive(data: InboundEmailQuery): {
  data: InboundEmailQuery;
} {
  return applyToDetail(data, undefined, transformUnarchive);
}

export function applyDetailOptimisticMarkAsRead(data: InboundEmailQuery): {
  data: InboundEmailQuery;
  wasUnread: boolean;
} {
  return applyToDetail(data, undefined, transformMarkAsRead);
}

export function applyDetailOptimisticAssignToProject(
  data: InboundEmailQuery,
  variables: {
    projectId: string;
    projectReference: string;
  }
): { data: InboundEmailQuery } {
  return applyToDetail(data, variables, transformAssignToProject);
}

export function applyDetailOptimisticUnassign(data: InboundEmailQuery): {
  data: InboundEmailQuery;
} {
  return applyToDetail(data, undefined, transformUnassign);
}
